"use client";
import { getImagePath } from "@/lib/utils";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

import ThemeToggler from "./ThemeToggler";
import { PARTNER_SIGNUP_ENABLED, SUPER_PARTNER_SIGNUP_ENABLED } from "@/components/constants/general";
import getMenuData from "./menuData";
// Type for menu translations
type MenuTranslations = {
  home: string;
  catalogue: string;
  howItWorks: string;
  more: string;
  findPartner: string;
  pricing: string;
  trackOrder: string;
  benefitsCustomer: string;
  benefitsPartner: string;
  ownAndEarn: string;
  benefitsProducer: string;
  login: string;
};
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { resetUser } from "@/lib/analytics";

type HeaderProps = {
  headerT: Record<string, string>;
  menuT: Record<string, string>;
};

const Header = ({ headerT, menuT }: HeaderProps) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [navbarOpen, setNavbarOpen] = useState(false);
  const [sticky, setSticky] = useState(false);
  const [openIndex, setOpenIndex] = useState(-1);
  const [isMobile, setIsMobile] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const isShopPartner =
    typeof window !== "undefined" &&
    window.location.pathname === "/shop-partner";

  const t = (key: string) => headerT[key] || key;
  const menuRaw = getMenuData(menuT as MenuTranslations);
  const menu = menuRaw.map((item) => {
    if (item.submenu && isLoggedIn) {
      return { ...item, submenu: item.submenu.filter((sub) => sub.path !== "/login") };
    }
    return item;
  });
  const usePathName = usePathname();

  const navbarToggleHandler = () => {
    setNavbarOpen(!navbarOpen);
  };

  const handleStickyNavbar = () => {
    if (window.scrollY >= 80) {
      setSticky(true);
    } else {
      setSticky(false);
    }
  };

  const handleSubmenu = (index) => {
    if (openIndex === index) {
      setOpenIndex(-1);
    } else {
      setOpenIndex(index);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleStickyNavbar);
    return () => window.removeEventListener("scroll", handleStickyNavbar);
  }, []);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 1024);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const check = () => setIsLoggedIn(!!localStorage.getItem("token"));
    check();
    window.addEventListener("authChanged", check as any);
    window.addEventListener("storage", check);
    return () => {
      window.removeEventListener("authChanged", check as any);
      window.removeEventListener("storage", check);
    };
  }, []);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest("#accountDropdown")) {
        setAccountOpen(false);
      }
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  const handleLogout = () => {
    resetUser();
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    window.dispatchEvent(new Event("authChanged"));
    window.location.href = "/login";
  };

  return (
    <header
      className={`header left-0 top-0 z-40 flex w-full items-center ${
        sticky
          ? "fixed z-[9999] bg-white !bg-opacity-80 shadow-sticky backdrop-blur-sm transition"
          : "absolute bg-transparent"
      }`}
      style={isShopPartner ? { display: "none" } : undefined}
    >
      <div className="container mx-auto px-4 md:py-5  max-w-screen-2xl">
        {/* Mobile Layout */}
        <div className="xl:hidden">
          {/* Top Row: Logo + Hamburger + Account Icon */}
          <div className="flex items-center justify-between mb-2">
            {/* Logo */}
            <Link href="/" className="flex-shrink-0">
              <div className="w-28 sm:w-32 h-auto">
                <Image
                  src={t("logo")}
                  alt="logo"
                  width={140}
                  height={50}
                  className="h-auto w-auto"
                  priority
                />
              </div>
            </Link>

            {/* Right Side: Account Icon + Hamburger */}
            <div className="flex items-center gap-3">
              {/* Account Icon (if logged in) */}
              {isLoggedIn && (
                <div className="relative" id="accountDropdown">
                  <button
                    type="button"
                    onClick={() => setAccountOpen((v) => !v)}
                    className="p-2 rounded-tl-xl rounded-br-xl border-2 border-primary hover:bg-primary/10 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </button>

                  {accountOpen && (
                    <div className="absolute right-0 top-full mt-2 w-52 bg-white border border-gray-200 rounded-xl shadow-xl z-[9999] overflow-hidden py-1">
                      <Link
                        href="/my-profile"
                        onClick={() => setAccountOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-primary/5 hover:text-primary transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        {t("myprofile")}
                      </Link>
                      <Link
                        href="/my-orders"
                        onClick={() => setAccountOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-primary/5 hover:text-primary transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                        </svg>
                        {t("myorders")}
                      </Link>
                      <div className="h-px bg-gray-100 mx-3 my-1" />
                      <button
                        type="button"
                        onClick={() => {
                          setAccountOpen(false);
                          handleLogout();
                        }}
                        className="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        {t("logout")}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Hamburger Menu */}
              <button
                onClick={navbarToggleHandler}
                id="navbarToggler"
                aria-label="Mobile Menu"
                className="rounded-lg px-3 py-2 ring-primary focus:ring-2"
              >
                <span
                  className={`block h-0.5 w-7 bg-primary transition-all duration-300 mb-1.5 ${
                    navbarOpen ? "rotate-45 translate-y-2" : ""
                  }`}
                />
                <span
                  className={`block h-0.5 w-7 bg-primary transition-all duration-300 mb-1.5 ${
                    navbarOpen ? "opacity-0" : ""
                  }`}
                />
                <span
                  className={`block h-0.5 w-7 bg-primary transition-all duration-300 ${
                    navbarOpen ? "-rotate-45 -translate-y-2" : ""
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Second Row: Buttons in horizontal layout */}

          {/* Mobile Menu Dropdown */}
          {navbarOpen && (
            <nav className="mt-3 bg-white rounded-lg border border-gray-200 shadow-lg p-4">
              <ul className="space-y-2">
                {menu.map((menuItem, index) => (
                  <li key={index}>
                    {menuItem.path ? (
                      <Link
                        href={menuItem.path}
                        className={`block py-2 px-3 rounded-lg ${
                          usePathName === menuItem.path
                            ? "text-primary bg-primary/10"
                            : "text-dark hover:text-primary hover:bg-gray-50"
                        }`}
                        onClick={() => setNavbarOpen(false)}
                      >
                        {menuItem.title}
                      </Link>
                    ) : (
                      <>
                        <button
                          onClick={() => handleSubmenu(index)}
                          className="flex w-full items-center justify-between py-2 px-3 text-dark hover:text-primary rounded-lg hover:bg-gray-50"
                        >
                          {menuItem.title}
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 25 24"
                            className={`transition-transform ${
                              openIndex === index ? "rotate-180" : ""
                            }`}
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M6.29289 8.8427C6.68342 8.45217 7.31658 8.45217 7.70711 8.8427L12 13.1356L16.2929 8.8427C16.6834 8.45217 17.3166 8.45217 17.7071 8.8427C18.0976 9.23322 18.0976 9.86639 17.7071 10.2569L12 15.964L6.29289 10.2569C5.90237 9.86639 5.90237 9.23322 6.29289 8.8427Z"
                              fill="currentColor"
                            />
                          </svg>
                        </button>
                        {openIndex === index && (
                          <div className="pl-4 mt-1 space-y-1">
                            {menuItem.submenu.map((submenuItem, subIndex) => (
                              <Link
                                href={submenuItem.path}
                                key={subIndex}
                                className="block py-2 px-3 text-sm text-dark hover:text-primary rounded-lg hover:bg-gray-50"
                                onClick={() => setNavbarOpen(false)}
                              >
                                {submenuItem.title}
                              </Link>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </li>
                ))}
              </ul>
              <div className="mt-3 pt-3 border-t border-gray-200 flex flex-col gap-2">
                <Link
                  href="/buynow-customer-campaign"
                  className="border-2 border-black text-black hover:border-primary hover:text-primary hover:bg-warm-50 font-bold px-4 py-2 rounded-br-2xl rounded-tl-2xl text-sm text-center"
                  onClick={() => setNavbarOpen(false)}
                >
                  {t("bookNow")}
                </Link>
                {PARTNER_SIGNUP_ENABLED && (
                  <Link
                    href="/own-and-earn"
                    className="border-2 border-black text-black hover:border-primary hover:text-primary hover:bg-warm-50 font-bold px-4 py-2 rounded-br-2xl rounded-tl-2xl text-sm text-center"
                    onClick={() => setNavbarOpen(false)}
                  >
                    {t("ownAndEarn")}
                  </Link>
                )}
                {SUPER_PARTNER_SIGNUP_ENABLED && (
                  <Link
                    href="/super-partner"
                    className="border-2 border-black text-black hover:border-primary hover:text-primary hover:bg-warm-50 font-bold px-4 py-2 rounded-br-2xl rounded-tl-2xl text-sm text-center"
                    onClick={() => setNavbarOpen(false)}
                  >
                    {t("superPartner")}
                  </Link>
                )}
                <Link
                  href="/join-producer"
                  className="border-2 border-black text-black hover:border-primary hover:text-primary hover:bg-warm-50 font-bold px-4 py-2 rounded-br-2xl rounded-tl-2xl text-sm text-center"
                  onClick={() => setNavbarOpen(false)}
                >
                  {t("joinProducer")}
                </Link>
                <LanguageSwitcher />
              </div>
            </nav>
          )}
        </div>

        {/* Desktop Layout */}
        <div className="hidden xl:flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex-shrink-0">
            <div className="w-32 h-auto">
              <Image
                src={t("logo")}
                alt="logo"
                width={140}
                height={50}
                className="h-auto w-auto"
                priority
              />
            </div>
          </Link>

          {/* Navigation */}
          <nav className="ml-auto mr-4">
            <ul className="flex items-center space-x-6">
              {menu.map((menuItem, index) => (
                <li key={index} className="relative group">
                  {menuItem.path ? (
                    <Link
                      href={menuItem.path}
                      className={`py-3 text-base ${
                        usePathName === menuItem.path
                          ? "text-primary"
                          : "text-dark hover:text-primary"
                      }`}
                    >
                      {menuItem.title}
                    </Link>
                  ) : (
                    <>
                      <button className="flex items-center py-3 text-base text-dark hover:text-primary">
                        {menuItem.title}
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 25 24"
                          className="ml-1"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M6.29289 8.8427C6.68342 8.45217 7.31658 8.45217 7.70711 8.8427L12 13.1356L16.2929 8.8427C16.6834 8.45217 17.3166 8.45217 17.7071 8.8427C18.0976 9.23322 18.0976 9.86639 17.7071 10.2569L12 15.964L6.29289 10.2569C5.90237 9.86639 5.90237 9.23322 6.29289 8.8427Z"
                            fill="currentColor"
                          />
                        </svg>
                      </button>
                      <div className="invisible absolute left-0 top-full pt-2 opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-300">
                        <div className="bg-white rounded-lg shadow-lg p-4 min-w-max">
                          {menuItem.submenu.map((submenuItem, subIndex) => (
                            <Link
                              href={submenuItem.path}
                              key={subIndex}
                              className="block py-2 px-3 text-sm text-dark hover:text-primary rounded hover:bg-gray-50 whitespace-nowrap"
                            >
                              {submenuItem.title}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          {/* Action Buttons - Responsive */}
          <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2 md:gap-3 w-full md:w-auto mt-2 md:mt-0">
            <Link
              href="/buynow-customer-campaign"
              className="flex-1 sm:flex-none border-2 border-black text-black hover:border-primary hover:text-primary hover:bg-warm-50 font-bold px-2 py-2 sm:px-4 sm:py-2 rounded-br-2xl rounded-tl-2xl text-xs sm:text-sm whitespace-nowrap text-center inline-flex items-center justify-center"
            >
              {t("bookNow")}
            </Link>
            {PARTNER_SIGNUP_ENABLED && (
              <Link
                href="/own-and-earn"
                className="flex-1 sm:flex-none border-2 border-black text-black hover:border-primary hover:text-primary hover:bg-warm-50 font-bold px-2 py-2 sm:px-4 sm:py-2 rounded-br-2xl rounded-tl-2xl text-xs sm:text-sm whitespace-nowrap text-center inline-flex items-center justify-center"
              >
                {t("ownAndEarn")}
              </Link>
            )}
            {SUPER_PARTNER_SIGNUP_ENABLED && (
              <Link
                href="/super-partner"
                className="flex-1 sm:flex-none border-2 border-black text-black hover:border-primary hover:text-primary hover:bg-warm-50 font-bold px-2 py-2 sm:px-4 sm:py-2 rounded-br-2xl rounded-tl-2xl text-xs sm:text-sm whitespace-nowrap text-center inline-flex items-center justify-center"
              >
                {t("superPartner")}
              </Link>
            )}
            <Link
              href="/join-producer"
              className="flex-1 sm:flex-none border-2 border-black text-black hover:border-primary hover:text-primary hover:bg-warm-50 font-bold px-2 py-2 sm:px-4 sm:py-2 rounded-br-2xl rounded-tl-2xl text-xs sm:text-sm whitespace-nowrap text-center inline-flex items-center justify-center"
            >
              {t("joinProducer")}
            </Link>
            <LanguageSwitcher />

            {isLoggedIn && (
              <div className="relative" id="accountDropdown">
                <button
                  type="button"
                  onClick={() => setAccountOpen((v) => !v)}
                  className="w-10 h-10 bg-primary hover:bg-primary/90 rounded-full flex items-center justify-center shadow-md hover:shadow-lg transition-all"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </button>

                {accountOpen && (
                  <div className="absolute right-0 top-full mt-2 w-52 bg-white border border-gray-200 rounded-xl shadow-xl z-[9999] overflow-hidden py-1">
                    <Link
                      href="/my-profile"
                      onClick={() => setAccountOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-primary/5 hover:text-primary transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      {t("myprofile")}
                    </Link>
                    <Link
                      href="/my-orders"
                      onClick={() => setAccountOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-primary/5 hover:text-primary transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                      </svg>
                      {t("myorders")}
                    </Link>
                    <div className="h-px bg-gray-100 mx-3 my-1" />
                    <button
                      type="button"
                      onClick={() => {
                        setAccountOpen(false);
                        handleLogout();
                      }}
                      className="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      {t("logout")}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
